/**
 * useRowPopover: open / pin / dismiss rules for a small popover hung off an
 * Order Queue row control (the flag button, the OOS status badge).
 *
 * Hovering the trigger with a mouse, focusing it from the keyboard, or tapping
 * it opens the popover. A mouse click pins it open (it stays after the pointer
 * leaves) until a second click, an outside click or Escape. Taps and Enter /
 * Space toggle it. Clicks, on the trigger or inside the popover, never reach the
 * row (which would expand it).
 *
 * `interactive` is for a popover holding a control the user has to reach:
 *  - leaving the trigger waits CLOSE_DELAY_MS before closing, and moving the
 *    pointer onto the popover cancels that, so the mouse can cross the gap;
 *  - Enter or Space on the trigger while open moves focus to `focusTargetRef`,
 *    and Escape from inside the popover closes it and puts focus back on the
 *    trigger (without reopening it).
 *
 * Spread `rootProps` on <Popover>, `triggerProps` on the trigger and
 * `contentProps` on <PopoverContent>.
 */
import {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';

const CLOSE_DELAY_MS = 120;

export function useRowPopover<FocusTarget extends HTMLElement = HTMLAnchorElement>({
  interactive = false,
}: { interactive?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  // Set by a mouse click: hover-out no longer closes it. Cleared on every close.
  const pinned = useRef(false);
  // Pointer type of the press that leads to the next click: a mouse click
  // must not toggle (hover already opened it); touch, pen and keyboard do.
  const lastPointer = useRef<string>('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const focusTargetRef = useRef<FocusTarget>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Escape from inside the popover: focus returns to the trigger on close.
  const refocusTrigger = useRef(false);
  // That programmatic focus must not reopen what Escape just closed.
  const skipFocusOpen = useRef(false);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => cancelClose, []);

  const close = () => {
    cancelClose();
    pinned.current = false;
    setOpen(false);
  };

  // The pointer left the trigger (or, when interactive, the popover).
  const hoverOut = () => {
    if (pinned.current) return;
    if (!interactive) {
      setOpen(false);
      return;
    }
    cancelClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      if (!pinned.current) setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const rootProps = {
    open,
    onOpenChange: (next: boolean) => {
      // Radix: Escape and an outside click.
      if (!next) close();
    },
  };

  const triggerProps = {
    ref: triggerRef,
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
      lastPointer.current = e.pointerType;
    },
    onPointerEnter: (e: PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType !== 'mouse') return;
      cancelClose();
      setOpen(true);
    },
    onPointerLeave: (e: PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === 'mouse') hoverOut();
    },
    onFocus: (e: FocusEvent<HTMLButtonElement>) => {
      if (skipFocusOpen.current) {
        skipFocusOpen.current = false;
        return;
      }
      // Keyboard focus only. A press (mouse, touch, pen) records its
      // pointer type before focus lands, and its click decides instead.
      if (lastPointer.current === '' && e.currentTarget.matches(':focus-visible')) setOpen(true);
    },
    onBlur: (e: FocusEvent<HTMLButtonElement>) => {
      lastPointer.current = '';
      // A press inside the content moves focus there (Radix FocusScope
      // makes it focusable): still inside the popover, so stay open.
      if (e.relatedTarget instanceof Node && contentRef.current?.contains(e.relatedTarget)) return;
      close();
    },
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
      if (!interactive || !open || (e.key !== 'Enter' && e.key !== ' ')) return;
      const target = focusTargetRef.current;
      if (!target) return;
      // Not a toggle: step into the popover instead of closing it.
      e.preventDefault();
      target.focus();
    },
    onClick: (e: MouseEvent<HTMLButtonElement>) => {
      // Own the toggle (Radix skips its own when default-prevented) and
      // keep the row from expanding.
      e.preventDefault();
      e.stopPropagation();
      if (lastPointer.current === 'mouse') {
        // Hover already opened it: the first click pins, the second closes.
        if (open && pinned.current) {
          close();
        } else {
          pinned.current = true;
          setOpen(true);
        }
      } else if (open) {
        close();
      } else {
        setOpen(true);
      }
      lastPointer.current = '';
    },
  };

  const contentProps = {
    ref: contentRef,
    onOpenAutoFocus: (e: Event) => e.preventDefault(),
    onCloseAutoFocus: (e: Event) => {
      e.preventDefault();
      if (!refocusTrigger.current) return;
      refocusTrigger.current = false;
      skipFocusOpen.current = true;
      triggerRef.current?.focus();
    },
    onEscapeKeyDown: () => {
      if (interactive && contentRef.current?.contains(document.activeElement)) refocusTrigger.current = true;
    },
    onPointerEnter: (e: PointerEvent<HTMLDivElement>) => {
      if (interactive && e.pointerType === 'mouse') cancelClose();
    },
    onPointerLeave: (e: PointerEvent<HTMLDivElement>) => {
      if (interactive && e.pointerType === 'mouse') hoverOut();
    },
    // Portalled, but React still bubbles it to the row, which would expand.
    onClick: (e: MouseEvent<HTMLDivElement>) => e.stopPropagation(),
  };

  return { rootProps, triggerProps, contentProps, focusTargetRef };
}
